// Cached API base URL loaded from aws-exports.json
let JOURNAL_ARTICLES_API_URL = ""

/**
 * Error thrown by journal-articles API calls, carrying the HTTP status and an
 * optional backend error `code` so callers can distinguish specific failures
 * (e.g. a collection the user is not allowed to access) from generic errors.
 */
export class JournalArticlesApiError extends Error {
  /** The HTTP status code of the failed response. */
  readonly status: number
  /** The backend-provided machine-readable error code, when present. */
  readonly code: string | null

  /**
   * @param message - Human-readable error message.
   * @param status - HTTP status code of the response.
   * @param code - Backend error code (e.g. "COLLECTION_FORBIDDEN"), or null.
   */
  constructor(message: string, status: number, code: string | null) {
    super(message)
    this.name = "JournalArticlesApiError"
    this.status = status
    this.code = code
  }
}

/**
 * The backend error code returned when the caller requests a collection they
 * are not a member of. Surfaced on JournalArticlesApiError.code.
 */
export const COLLECTION_FORBIDDEN_CODE = "COLLECTION_FORBIDDEN"

/**
 * Load the journal articles API URL from aws-exports.json configuration.
 *
 * Falls back to a relative /api/biblio/journal-articles path for local development
 * when no explicit API URL is configured.
 *
 * @returns The full URL for the journal articles API endpoint.
 */
async function loadApiUrl(): Promise<string> {
  if (JOURNAL_ARTICLES_API_URL) {
    return JOURNAL_ARTICLES_API_URL
  }

  try {
    const response = await fetch("/aws-exports.json")
    const config = await response.json()
    // VITE_API_URL env var takes precedence (used with npm run dev --host).
    // Falls back to apiUrl from aws-exports.json (CI-generated for deployed envs).
    // Last resort: relative path (will 404 locally but works behind CloudFront).
    const baseUrl = import.meta.env.VITE_API_URL || config.apiUrl || ""
    JOURNAL_ARTICLES_API_URL = baseUrl ? `${baseUrl}/api/biblio/journal-articles` : "/api/biblio/journal-articles"
    return JOURNAL_ARTICLES_API_URL
  } catch (error) {
    console.error("Failed to load API URL from aws-exports.json:", error)
    throw new Error("journal-articles API URL not configured")
  }
}

export interface Author {
  name: string
}

export interface TopicInfo {
  topic: string | null
  status: string | null
  labelledOn: string | null
  labelledBy: string | null
}

export interface CollectionInfo {
  collectionId: number | null
  addedOn: string | null
  addedBy: string | null
  name: string | null
}

export const extractionStatusLabelMap: Record<string, string> = {
  "NOT_STARTED": "Not Started",
  "IN_PROGRESS": "In Progress",
  "EXTRACTED": "Extracted",
  "EDITING": "Editing",
  "EXCLUDED": "Excluded",
  "EXTRACTED_VIA_COMMS": "Extracted via Personal Comm",
}


export const uploadStatusLabelMap: Record<string, string> = {
  "PENDING_UPLOAD": "Pending Upload",
  "PENDING_REUPLOAD": "Pending Reupload",
  "UPLOADED": "Uploaded",
  "REUPLOADED": "Reuploaded",
}

export const authorContactStatusLabelMap: Record<string, string> = {
  "WAITING": "Waiting",
  "NEEDS_ACTION": "Needs Action",
  "CLOSED": "Closed",
}

export const exclusionReasonLabelMap: Record<string, string> = {
  "TESTED_ONLY_ASYMPTOMATIC": 'Tested only asymptomatic',
  "TESTED_ONLY_SYMPTOMATIC": 'Tested only symptomatic',
  "TEST_AFFECTED_BY_AN_INTERVENTION": 'Test affected by an intervention',
  "DATA_HAS_BEEN_OBTAINED_FROM_ANOTHER_SOURCE": 'Data has been obtained from another source',
  "TESTED_ONLY_COINFECTIONS": 'Tested only coinfections',
  "TESTED_ONLY_PREGNANT_WOMEN": 'Tested only pregnant women',
  "TESTED_ONLY_MIGRANTS_OR_SPECIFIC_ETHNIC_GROUPS_OR_OCCUPATIONS": 'Tested only migrants or specific ethnic groups or occupations',
  "NO_PREVALENCE_DATA": 'No prevalence data',
  "DUPLICATE_SOURCE": 'Duplicate source',
  "DATA_ACQUIRED_FROM_ANOTHER_SOURCE": 'Data acquired from another source',
  "NO_FULL_TEXT_FILE_FOUND": 'No full text file found',
  "AUTHOR_COMMUNICATION_UNSUCCESSFUL": 'Author communication unsuccessful'
}

export const topicLabelMap: Record<string, string> = {
  "pr": "PR",
  "api": "API",
  "ir": "IR",
  "seasonality": "Seasonality"
}

export const sourceTypeLabelMap: Record<string, string> = {
  "bookSection": "Book Section",
  "report": "Report",
  "thesis": "Thesis",
  "webPage": "Web Page",
  "personalCommunication": "Personal Communication",
  "article": "Article",
  "conferencePaper": "Conference Paper"
}

export interface TopicHistory {
  topic: string
  status: boolean
  createdBy: string
  createdDate: string
}

export interface Note {
  noteText: string
  createdBy: string
  createdDate: string
}

export interface JournalArticle {
  sourceId: number
  abstract: string
  authors: Author[]
  citation: string
  city: string
  communicationDate: string
  contacts: string
  createdBy: string
  createdDate: string
  currentValue: string
  dateUrlVisited: string
  doi: string
  doiUrl: string
  ihmeNid: string
  institution: string
  // Articles carry a single string; reports store a list of institution names.
  // Render through formatInstitutions() rather than directly.
  institutions: string | string[]
  issue: string
  journaltitle: string
  lastModifiedBy: string
  lastModifiedDate: string
  mapSurveyId: string
  pages: string
  publicationYear: number
  pubmedId: string
  pubmedUrl: string
  reportNumber: string
  // Report classification key (sourceType "report" only); absent for reports that need review.
  reportType?: string
  researchNotes: string
  sourceType: string
  suggestedCitation: string
  surveyType: string
  title: string
  url: string
  usagePermission: string
  volume: string
  coversPRTopic: boolean
  coversAPITopic: boolean
  coversIRTopic: boolean
  coversSeasonalityTopic: boolean
  scorePRTopic?: number
  topicHistory: TopicHistory[]
  topics: string[]
  nonTopics: string[]
  collectionIds: number[]
  notes: Note[]
  continents?: string[]
  countries?: string[]
  studyYears?: string[]
  dateEntities?: string[]
  locationEntities?: string[]
  prAuthorContactLastUpdatedBy: string | null
  prAuthorContactLastUpdatedOn: string | null
  prAuthorContactStatus: string | null
  prExclusionLastUpdatedBy: string | null
  prExclusionLastUpdatedOn: string | null
  prExclusionReason: string | null
  prExtractionFileId: string | null
  prExtractionLastUpdatedBy: string | null
  prExtractionLastUpdatedOn: string | null
  prExtractionStatus: string | null
  prUploadLastUpdatedBy: string | null
  prUploadLastUpdatedOn: string | null
  prUploadStatus: string | null
  rsExtractionFileId: string | null
  rsExtractionLastUpdatedBy: string | null
  rsExtractionLastUpdatedOn: string | null
  rsExtractionStatus: string | null
  rsUploadLastUpdatedBy: string | null
  rsUploadLastUpdatedOn: string | null
  rsUploadStatus: string | null
}

/**
 * Format a source's `institutions` value as display text.
 *
 * Articles store institutions as a single string, while reports store a list
 * of names, so callers should render through this helper instead of the raw
 * value (which would otherwise print an array as "A,B").
 *
 * @param value - The `institutions` field: a string, a list of strings, or
 *   undefined/null when the source has none.
 * @returns The list joined with "; ", the string unchanged, or "" when empty.
 */
export function formatInstitutions(value: string | string[] | undefined | null): string {
  if (value == null) return ""
  return Array.isArray(value) ? value.join("; ") : value
}

/**
 * Response shape from the paginated journal articles API endpoint.
 * Includes optional filter echo fields (topic, collectionId, createdAfter)
 * that reflect the filters applied in the request.
 */
export interface JournalArticlesResponse {
  items: JournalArticle[]
  totalCount: number
  page: number
  pageSize: number
  totalPages: number
  topic: string | null
  collectionId: number | null
  searchTerm: string | null
  createdAfter: string | null
}

/**
 * Optional filter parameters for the journal articles API.
 */
export interface JournalArticlesFilters {
  /** Comma-separated topic slugs to filter by (e.g. "pr", "pr,ir"). */
  topic?: string
  /**
   * Collection ID(s) to filter by. A single numeric id (when viewing one
   * collection) or a comma-separated string of ids (when filtering by multiple
   * collections, OR logic). Serialised verbatim into the query string.
   */
  collectionId?: number | string
  /** Case-insensitive search term to filter across sourceId, title, citation, doi, and pubmedId. */
  searchTerm?: string
  /**
   * ISO 8601 date or datetime string. When provided, only articles with
   * createdDate >= this value are returned. Accepts YYYY-MM-DD or full
   * ISO datetime (e.g. "2026-07-09" or "2026-07-09T00:00:00+00:00").
   */
  createdAfter?: string
  /**
   * Comma-separated prExtractionStatus values to include
   * (e.g. "NOT_STARTED,IN_PROGRESS,EXTRACTED,EDITING,EXTRACTED_VIA_COMMS").
   * Only articles whose prExtractionStatus matches one of these values are returned.
   * Valid values: NOT_STARTED, IN_PROGRESS, EXTRACTED, EDITING, EXCLUDED, EXTRACTED_VIA_COMMS.
   */
  prExtractionStatus?: string
  /**
   * Comma-separated prUploadStatus values to include
   * (e.g. "PENDING_UPLOAD,PENDING_REUPLOAD").
   * Only articles whose prUploadStatus matches one of these values are returned.
   * Valid values: PENDING_UPLOAD, PENDING_REUPLOAD, UPLOADED, REUPLOADED.
   */
  prUploadStatus?: string
  /**
   * Comma-separated prContactStatus values to include
   * (e.g. "WAITING,NEEDS_ACTION").
   * Only articles whose prContactStatus matches one of these values are returned.
   * Valid values: WAITING, NEEDS_ACTION, CLOSED.
   */
  prContactStatus?: string
  /**
   * Comma-separated rsExtractionStatus values to include
   * (e.g. "NOT_STARTED,IN_PROGRESS,EXTRACTED").
   * Only articles whose rsExtractionStatus matches one of these values are returned.
   * Valid values: NOT_STARTED, IN_PROGRESS, EXTRACTED, EDITING, EXCLUDED, EXTRACTED_VIA_COMMS.
   */
  rsExtractionStatus?: string
  /**
   * Comma-separated continent names to filter by (e.g. "Africa,Asia").
   * Only articles whose continents array contains at least one of the
   * specified values are returned (OR logic).
   */
  continents?: string
  /**
   * Comma-separated country names to filter by (e.g. "Kenya,Nigeria").
   * Only articles whose countries array contains at least one of the
   * specified values are returned (OR logic).
   */
  countries?: string
  /**
   * Comma-separated study year values to filter by (e.g. "2019,2020").
   * Only articles whose studyYears array contains at least one of the
   * specified values are returned (OR logic).
   */
  studyYears?: string
  /**
   * When true, only articles that have a scorePRTopic value set are returned.
   * This is the base filter for the PR Bulk Review view (PR-classifier-scored
   * articles).
   */
  scorePRTopicExists?: boolean
  /**
   * Numeric threshold. When provided, only articles whose scorePRTopic is
   * strictly greater than this value are returned (e.g. 0.5).
   */
  scorePRTopicGt?: number
  /**
   * Filter by the human-set coversPRTopic flag, distinguishing an explicit
   * false from an absent value. One of "true", "false", or "missing".
   */
  coversPRTopicIs?: "true" | "false" | "missing"
  /**
   * Field name to sort results by. Valid values: "publicationYear",
   * "scorePRTopic" (the raw PR-topic classifier score, used by the PR Bulk
   * Review view). When omitted, results are sorted by createdDate descending
   * (default).
   */
  sortBy?: string
  /**
   * Sort direction: "asc" for ascending, "desc" for descending.
   * Only applies when sortBy is provided.
   */
  sortDirection?: "asc" | "desc"
}

/**
 * Fetch a page of journal articles from the GET /api/biblio/journal-articles endpoint.
 * Supports optional filtering by topic(s), collectionId, searchTerm, and/or
 * createdAfter via query parameters.
 *
 * The request is routed through CloudFront to the API Gateway.
 * Results are sorted by createdDate descending (newest first).
 *
 * @param idToken - Auth0 access token for API authentication.
 * @param page - 1-based page number (default 1).
 * @param pageSize - Number of items per page (default 50, max 200).
 * @param filters - Optional filters: topic (comma-separated), collectionId,
 *                  searchTerm, and/or createdAfter (ISO 8601 date or datetime).
 * @returns Paginated response with items, totalCount, page, pageSize, totalPages,
 *          and the applied filter values (topic, collectionId, searchTerm, createdAfter).
 * @throws Error if the API call fails or returns a non-OK status.
 */
export async function getJournalArticles(
  idToken: string,
  page: number = 1,
  pageSize: number = 50,
  filters?: JournalArticlesFilters
): Promise<JournalArticlesResponse> {
  const apiUrl = await loadApiUrl()

  // Build query string with pagination and optional filter parameters
  const params = new URLSearchParams()
  params.set("page", String(page))
  params.set("pageSize", String(pageSize))

  if (filters?.topic) {
    params.set("topic", filters.topic)
  }
  if (filters?.collectionId !== undefined) {
    params.set("collectionId", String(filters.collectionId))
  }
  if (filters?.searchTerm) {
    params.set("searchTerm", filters.searchTerm)
  }
  if (filters?.createdAfter) {
    params.set("createdAfter", filters.createdAfter)
  }
  if (filters?.prExtractionStatus) {
    params.set("prExtractionStatus", filters.prExtractionStatus)
  }
  if (filters?.prUploadStatus) {
    params.set("prUploadStatus", filters.prUploadStatus)
  }
  if (filters?.prContactStatus) {
    params.set("prContactStatus", filters.prContactStatus)
  }
  if (filters?.rsExtractionStatus) {
    params.set("rsExtractionStatus", filters.rsExtractionStatus)
  }
  if (filters?.continents) {
    params.set("continents", filters.continents)
  }
  if (filters?.countries) {
    params.set("countries", filters.countries)
  }
  if (filters?.studyYears) {
    params.set("studyYears", filters.studyYears)
  }
  if (filters?.scorePRTopicExists) {
    params.set("scorePRTopicExists", "true")
  }
  if (filters?.scorePRTopicGt !== undefined) {
    params.set("scorePRTopicGt", String(filters.scorePRTopicGt))
  }
  if (filters?.coversPRTopicIs) {
    params.set("coversPRTopicIs", filters.coversPRTopicIs)
  }
  if (filters?.sortBy) {
    params.set("sortBy", filters.sortBy)
  }
  if (filters?.sortDirection) {
    params.set("sortDirection", filters.sortDirection)
  }

  const response = await fetch(`${apiUrl}?${params.toString()}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${idToken}`,
    },
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new JournalArticlesApiError(
      errorData.error || `HTTP error! status: ${response.status}`,
      response.status,
      errorData.code ?? null
    )
  }

  const data: JournalArticlesResponse = await response.json()

  const sourcesWithTopic: JournalArticle[] = data.items.map(article => ({...article, topics: getTopicsAsList(article), nonTopics: getNonTopicsAsList(article)}))
  data.items = sourcesWithTopic

  return data
}

/**
 * The maximum page size the journal-articles API accepts in a single request.
 * Used when paging through the full result set to minimise the number of calls.
 */
const MAX_API_PAGE_SIZE: number = 200

/**
 * The base filters that define the "Open Author Contacts" view. These mirror
 * the filters applied by the list fetch in JournalArticlesContext for that view
 * and must stay in sync with it: the view shows PR-topic articles that are mid
 * extraction and not yet uploaded. Keeping them here lets both the paged list
 * fetch and the full-set facet fetch share the same definition.
 */
export const OPEN_AUTHOR_CONTACTS_BASE_FILTERS: Readonly<
  Pick<JournalArticlesFilters, "topic" | "prExtractionStatus" | "prUploadStatus">
> = {
  topic: "pr",
  prExtractionStatus: "IN_PROGRESS,EXTRACTED,EDITING,EXTRACTED_VIA_COMMS",
  prUploadStatus: "NONE,PENDING_UPLOAD",
}

/**
 * Fetch every journal article in the "Open Author Contacts" view by paging
 * through the API until all pages have been retrieved.
 *
 * Only the view's base filters are applied (see
 * {@link OPEN_AUTHOR_CONTACTS_BASE_FILTERS}); no continent/country/year filter
 * is applied, so the returned set is the complete dataset the facet option
 * lists are derived from. The dataset for this view is expected to be small
 * (a few pages at most), so loading it in full is cheap.
 *
 * @param idToken - Auth0 access token for API authentication.
 * @returns The complete list of articles matching the view's base filters.
 * @throws Error if any underlying API call fails.
 */
export async function getAllOpenAuthorContactsArticles(
  idToken: string
): Promise<JournalArticle[]> {
  const allItems: JournalArticle[] = []
  let page: number = 1
  let totalPages: number = 1

  // Page through the result set. `totalPages` is read from the first response
  // and the loop continues until every page has been fetched. Guarded by the
  // reported total rather than an open-ended "until empty" loop so a backend
  // that returns a trailing empty page cannot spin forever.
  do {
    const response: JournalArticlesResponse = await getJournalArticles(
      idToken,
      page,
      MAX_API_PAGE_SIZE,
      { ...OPEN_AUTHOR_CONTACTS_BASE_FILTERS }
    )
    allItems.push(...response.items)
    totalPages = response.totalPages
    page += 1
  } while (page <= totalPages)

  return allItems
}

/**
 * Fetch a single journal article by its source ID (biblio ID).
 *
 * Uses the paginated endpoint with a searchTerm filter set to the
 * sourceId string, which matches against the sourceId field. Expects
 * exactly one result.
 *
 * @param idToken - Auth0 access token for API authentication.
 * @param sourceId - The numeric source (biblio) ID of the article.
 * @returns The matching JournalArticle.
 * @throws Error if the article is not found or the API call fails.
 */
export async function getJournalArticleById(
  idToken: string,
  sourceId: number
): Promise<JournalArticle> {
  const response = await getJournalArticles(idToken, 1, 1, {
    searchTerm: String(sourceId),
  })

  const article = response.items.find((a) => a.sourceId === sourceId)
  if (!article) {
    throw new Error(`Journal article with source ID ${sourceId} not found`)
  }

  return article
}

function getTopicsAsList(journalArticle: JournalArticle) {
  let list: string[] = []
  if(journalArticle.coversPRTopic) {
    list.push("pr")
  }
  if(journalArticle.coversAPITopic) {
    list.push("api")
  }
    if(journalArticle.coversIRTopic) {
    list.push("ir")
  }
    if(journalArticle.coversSeasonalityTopic) {
    list.push("seasonality")
  }
  return list
}

function getNonTopicsAsList(journalArticle: JournalArticle) {
  let list: string[] = []
  if(journalArticle.coversPRTopic == false) {
    list.push("pr")
  }
  if(journalArticle.coversAPITopic == false) {
    list.push("api")
  }
    if(journalArticle.coversIRTopic == false) {
    list.push("ir")
  }
    if(journalArticle.coversSeasonalityTopic == false) {
    list.push("seasonality")
  }
  return list
}



/**
 * Response from the POST /api/biblio/topics endpoint.
 */
export interface UpdateTopicsResponse {
  /** Success message from the API. */
  message: string
  /** The source ID that was updated. */
  sourceId: number
  /** The topics that were updated with their new values. */
  topics: Record<string, boolean>
}

/**
 * Update topic flags on a journal article via POST /api/biblio/topics.
 *
 * Sets the specified topic boolean flags on the source record and appends
 * entries to topicHistory. Only the topics provided are updated; others
 * are left unchanged.
 *
 * Valid topic keys: "PR", "API", "IR", "Seasonality".
 *
 * @param idToken - Auth0 access token for API authentication.
 * @param sourceId - The numeric source ID to update.
 * @param topics - Object mapping topic keys to boolean values.
 * @param createdBy - The display name/email of the user making the change.
 * @returns The API response confirming the update.
 * @throws Error if the API call fails or returns a non-OK status.
 */
export async function updateTopics(
  idToken: string,
  sourceId: number,
  topics: Record<string, boolean>,
  createdBy: string
): Promise<UpdateTopicsResponse> {
  const journalArticlesUrl = await loadApiUrl()
  // Derive the base biblio URL from the journal-articles URL
  const topicsUrl = journalArticlesUrl.replace("/journal-articles", "/topics")

  const response = await fetch(topicsUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({
      sourceId,
      topics,
      createdBy,
    }),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
  }

  const data: UpdateTopicsResponse = await response.json()
  return data
}

// ---------------------------------------------------------------------------
// Source IDs
// ---------------------------------------------------------------------------

/**
 * Response shape from GET /api/biblio/journal-articles/source-ids.
 */
export interface JournalArticleSourceIdsResponse {
  /** Array of all journal article source IDs in the database. */
  sourceIds: number[]
  /** Total count of source IDs returned. */
  totalCount: number
}

/**
 * Fetch the complete list of journal article source IDs from the backend.
 * Returns only the numeric IDs (no other article data), making the response
 * compact and fast.
 *
 * @param idToken - Auth0 access token for API authentication.
 * @returns Response containing the full array of source IDs and total count.
 * @throws Error if the API call fails or returns a non-OK status.
 */
export async function getJournalArticleSourceIds(
  idToken: string
): Promise<JournalArticleSourceIdsResponse> {
  const apiUrl = await loadApiUrl()

  const response = await fetch(`${apiUrl}/source-ids`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${idToken}`,
    },
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
  }

  const data: JournalArticleSourceIdsResponse = await response.json()
  return data
}


/**
 * Per-derived-category article counts, keyed by category. Every key is present
 * (zero-filled) in a response. The category keys match those returned by the
 * `GET /api/biblio/journal-articles/status-counts` endpoint and drawn by the
 * PR lit review summary chart.
 */
export interface StatusCounts {
  UPLOADED: number
  EXTRACTED: number
  WAITING_FOR_AUTHOR: number
  IN_PROGRESS: number
  NOT_STARTED: number
  EXCLUDED: number
}

/**
 * Response shape from GET /api/biblio/journal-articles/status-counts.
 */
export interface JournalArticleStatusCountsResponse {
  /** The collection the counts are for. */
  collectionId: number
  /** The topic slug the counts are filtered to. */
  topic: string
  /** Total number of topic-covering articles counted in the collection. */
  total: number
  /** Count per derived status category. */
  statusCounts: StatusCounts
}

/**
 * Fetch the per-status-category counts for a collection's topic-covering
 * articles from the GET /api/biblio/journal-articles/status-counts endpoint.
 *
 * This is a lightweight aggregate: the backend counts the articles server-side
 * and returns only the per-category totals, so the caller does not have to
 * fetch (and page through) every full article record just to draw a summary.
 *
 * @param idToken - Auth0 access token for API authentication.
 * @param collectionId - The numeric collection ID to aggregate.
 * @param topic - The topic slug to filter by (e.g. "pr").
 * @returns The collection's status-category counts.
 * @throws Error if the API call fails or returns a non-OK status.
 */
export async function getJournalArticleStatusCounts(
  idToken: string,
  collectionId: number,
  topic: string
): Promise<JournalArticleStatusCountsResponse> {
  const apiUrl = await loadApiUrl()

  const params = new URLSearchParams()
  params.set("collectionId", String(collectionId))
  params.set("topic", topic)

  const response = await fetch(`${apiUrl}/status-counts?${params.toString()}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${idToken}`,
    },
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new JournalArticlesApiError(
      errorData.error || `HTTP error! status: ${response.status}`,
      response.status,
      errorData.code ?? null
    )
  }

  const data: JournalArticleStatusCountsResponse = await response.json()
  return data
}
